import React from "react";
import { Row, Col, Tag, Divider, Empty, Table, Typography } from "antd";
import {
  BankOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import THEME from "../theme";
import { formatMoney, formatDate } from "../utils";
import StyledCard from "../components/StyledCard";
import StatBox from "../components/StatBox";
import InfoItem from "../components/InfoItem";

const { Text } = Typography;

// คอลัมน์ตารางการชำระเงิน (ก่อนรีไฟแนนซ์) — รวมรับปกติ + ลูกหนี้อื่นๆ, งวด, ต้น/ดอก/ส่วนลด, ยอดต้นคงเหลือ, สถานะบิล
const rejudgePaymentColumns = [
  {
    title: "ลำดับ",
    key: "index",
    width: 60,
    align: "center",
    render: (_, __, idx) => idx + 1,
  },
  {
    title: "เลขที่บิล",
    dataIndex: "TMBILL",
    key: "TMBILL",
    render: (val) => <Text style={{ fontFamily: "monospace" }}>{val}</Text>,
  },
  {
    title: "ประเภท",
    dataIndex: "__paymentGroup",
    key: "__paymentGroup",
    align: "center",
    width: 100,
    render: (val) => (
      <Tag color={val === "other" ? "purple" : "blue"}>
        {val === "other" ? "ลูกหนี้อื่นๆ" : "รับปกติ"}
      </Tag>
    ),
  },
  {
    title: "สถานะ",
    dataIndex: "FLAG",
    key: "FLAG",
    align: "center",
    width: 90,
    render: (val, record) => (
      <Tag color={val === "C" ? "red" : "green"}>
        {val === "C"
          ? `ยกเลิก${record.CANDT ? ` (${formatDate(record.CANDT)})` : ""}`
          : "รับแล้ว"}
      </Tag>
    ),
  },
  {
    title: "งวดที่",
    dataIndex: "NOPAY",
    key: "NOPAY",
    align: "center",
    width: 70,
  },
  {
    title: "รายการ",
    dataIndex: "FORDESC",
    key: "FORDESC",
  },
  {
    title: "วันที่ชำระ",
    dataIndex: "PAYDT",
    key: "PAYDT",
    render: (val) => formatDate(val),
  },
  {
    title: "ยอดเรียกเก็บ",
    dataIndex: "PAYAMT",
    key: "PAYAMT",
    align: "right",
    render: (val) => `${formatMoney(val)} บาท`,
  },
  {
    title: "ชำระต้น",
    dataIndex: "PAYAMT_N",
    key: "PAYAMT_N",
    align: "right",
    render: (val) => `${formatMoney(val)} บาท`,
  },
  {
    title: "ชำระดอก",
    dataIndex: "PAYAMT_V",
    key: "PAYAMT_V",
    align: "right",
    render: (val) => `${formatMoney(val)} บาท`,
  },
  {
    title: "ส่วนลด (ยอดต่าง)",
    dataIndex: "DISCT",
    key: "DISCT",
    align: "right",
    render: (val) =>
      Number(val) > 0 ? (
        <Text style={{ color: THEME.danger }}>-{formatMoney(val)} บาท</Text>
      ) : (
        "-"
      ),
  },
  {
    title: "รับสุทธิ",
    dataIndex: "NETPAY",
    key: "NETPAY",
    align: "right",
    render: (val) => (
      <Text strong style={{ color: THEME.success }}>
        {formatMoney(val)} บาท
      </Text>
    ),
  },
  {
    title: "ต้นคงเหลือหลังชำระ",
    dataIndex: "NEXTCAPITAL",
    key: "NEXTCAPITAL",
    align: "right",
    render: (val) => `${formatMoney(val)} บาท`,
  },

  {
    title: "ผู้รับ",
    dataIndex: "INPUT_BY",
    key: "INPUT_BY",
    align: "center",
    render: (val) => <Tag color="blue">{val}</Tag>,
  },
];

// สรุปยอดรวม (ไม่รวมบิลที่ยกเลิก)
const buildRejudgePaymentSummary = (data) => () => {
  const rows = (data || []).filter((r) => r.FLAG !== "C");
  const sumBy = (field) =>
    rows.reduce((sum, r) => sum + Number(r[field] || 0), 0);

  const MoneyCell = ({ value, index, color }) => (
    <Table.Summary.Cell index={index} align="right">
      <Text strong style={color ? { color } : undefined}>
        {formatMoney(value)} บาท
      </Text>
    </Table.Summary.Cell>
  );

  return (
    <Table.Summary.Row style={{ background: THEME.light }}>
      {/* ลำดับ, เลขที่บิล, ประเภท, สถานะ, งวดที่, รายการ, วันที่ชำระ */}
      <Table.Summary.Cell index={0} colSpan={7}>
        <Text strong>รวมทั้งหมด (ไม่รวมยกเลิก)</Text>
      </Table.Summary.Cell>
      <MoneyCell index={7} value={sumBy("PAYAMT")} />
      <MoneyCell index={8} value={sumBy("PAYAMT_N")} />
      <MoneyCell index={9} value={sumBy("PAYAMT_V")} />
      <MoneyCell index={10} value={sumBy("DISCT")} color={THEME.danger} />
      <MoneyCell index={11} value={sumBy("NETPAY")} color={THEME.success} />
      {/* ต้นคงเหลือหลังชำระ, ผู้รับ */}
      <Table.Summary.Cell index={12} colSpan={2} />
    </Table.Summary.Row>
  );
};

const rejudgePaymentPagination = {
  defaultPageSize: 5,
  showSizeChanger: true,
  pageSizeOptions: ["5", "10", "20", "50", "100"],
};

const LoanRejudgeTab = ({ judgeData, loanData, rejudgeData }) => {
  const customer = rejudgeData?.judge?.[0];
  const loanNew = rejudgeData?.loanNew;
  const rejudgePayments = rejudgeData?.chqtran || [];
  const rejudgeOtherPayments = rejudgeData?.chqtran_arothr || [];

  // รวมรายการรับปกติ + ลูกหนี้อื่นๆ เป็นตารางเดียว เรียงตามวันที่ชำระ
  const allRejudgePayments = [
    ...rejudgePayments.map((r) => ({ ...r, __paymentGroup: "normal" })),
    ...rejudgeOtherPayments.map((r) => ({ ...r, __paymentGroup: "other" })),
  ].sort((a, b) => new Date(a.PAYDT || 0) - new Date(b.PAYDT || 0));

  // รายการชำระล่าสุด (ไม่นับบิลที่ยกเลิก)
  const validRejudgePayments = allRejudgePayments.filter((r) => r.FLAG !== "C");
  const latestRejudgePayment =
    validRejudgePayments[validRejudgePayments.length - 1];

  if (!customer) return <Empty description="ไม่มีข้อมูล" />;
  return (
    <>
      <StyledCard
        title="ข้อมูลสัญญาก่อนรีไฟแนนซ์"
        icon={<BankOutlined style={{ color: THEME.primary, fontSize: 18 }} />}
        style={{ marginBottom: 16 }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={8} md={6}>
            <StatBox
              title="ยอดจัดทั้งหมด"
              value={customer?.totalJugde}
              color={THEME.primary}
              icon={<DollarOutlined />}
            />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <StatBox
              title="ชำระแล้ว"
              value={customer?.PAY}
              color={THEME.info}
              icon={<BankOutlined />}
            />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <StatBox
              title="ค่างวด"
              value={loanNew?.TOT_UPAY}
              color={THEME.success}
              icon={<CheckCircleOutlined />}
            />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <StatBox
              title="คงเหลือ"
              value={customer.TONBALANCE}
              color={THEME.danger}
              icon={<WarningOutlined />}
            />
          </Col>
        </Row>
        <Divider style={{ margin: "20px 0" }} />
        <Row gutter={[32, 16]}>
          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="วันทำสัญญา"
              value={formatDate(loanNew?.SDATE)}
              icon={<CalendarOutlined />}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem label="งวดแรก" value={formatDate(loanNew?.FDATE)} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem label="งวดสุดท้าย" value={formatDate(loanNew?.LDATE)} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem label="จำนวนงวด" value={`${loanNew?.T_NOPAY || 0} งวด`} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="งวดละ"
              value={`${formatMoney(loanNew?.TOT_UPAY)} บาท`}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="ชำระล่าสุด"
              value={formatDate(latestRejudgePayment?.PAYDT)}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="ยอดชำระล่าสุด"
              value={`${formatMoney(latestRejudgePayment?.NETPAY)} บาท`}
            />
          </Col>

          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="สัญญาเก่าคือ"
              value={`${judgeData?.judge?.[0]?.CONTNO || "-"}`}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="สัญญาใหม่"
              value={`${judgeData?.judge?.[0]?.newContno || "-"}`}
            />
          </Col>
        </Row>
      </StyledCard>

      {allRejudgePayments.length > 0 && (
        <StyledCard
          title="ตารางการชำระเงิน (ก่อนรีไฟแนนซ์)"
          icon={
            <DollarOutlined style={{ color: THEME.primary, fontSize: 18 }} />
          }
          style={{ marginBottom: 16 }}
        >
          <Table
            dataSource={allRejudgePayments}
            columns={rejudgePaymentColumns}
            rowKey={(record) => `${record.__paymentGroup}-${record.wctId}`}
            pagination={rejudgePaymentPagination}
            size="middle"
            scroll={{ x: 1500 }}
            summary={buildRejudgePaymentSummary(allRejudgePayments)}
          />
        </StyledCard>
      )}
    </>
  );
};

export default LoanRejudgeTab;
